import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ScanLine, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      <div className="hidden lg:block relative overflow-hidden border-r border-white/10">
        <img
          src="https://images.unsplash.com/photo-1727238451791-af16e7f7999b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Parking structure"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2">
            <img src="/zentara-logo.png" alt="Zentara Integrity" className="w-7 h-7 object-contain" />
            <span className="font-display font-bold text-xl">Zentara <span className="text-[#C5A24A]">Integrity</span></span>
          </div>
          <div>
            <h1 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight leading-[1.05]">
              Engineer-grade<br />concrete inspections.
            </h1>
            <p className="mt-4 text-muted-foreground max-w-md">
              Pin and classify structural defects with AI, measure in real 3D space, and export
              professional engineer-grade inspection reports.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/zentara-logo.png" alt="Zentara Integrity" className="w-7 h-7 object-contain" />
            <span className="font-display font-bold text-xl">Zentara <span className="text-[#C5A24A]">Integrity</span></span>
          </div>
          <p className="label-caps text-muted-foreground mb-2">Inspector Access</p>
          <h2 className="font-display text-2xl font-bold tracking-tight mb-6">Sign in to continue</h2>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="label-caps text-muted-foreground">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 bg-[#121212] border-white/10 h-11"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="label-caps text-muted-foreground">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 bg-[#121212] border-white/10 h-11"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 bg-[#C5A24A] hover:bg-[#A8863A] text-white font-semibold transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
